/**
 * Leaderboard Page - The Intelligent Standard
 *
 * Research-grade model rankings with confidence interval visualization.
 */

import {
  type JudgeConfig,
  MIN_MATCHES_FOR_RANKING,
} from '@meridian-ssa/shared';
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { Footer } from '@/components/Footer';
import {
  AlertCircleIcon,
  ChartIcon,
  ChevronRightIcon,
  InfoIcon,
} from '@/components/icons/common';
import { LogoAnimated } from '@/components/Logo';
import { HeroPattern } from '@/components/shared/HeroPattern';
import { ProviderLogo } from '@/components/shared/ProviderLogo';
import { SegmentedControl } from '@/components/ui/SegmentedControl';
import { apiRequest } from '@/config';
import { useIsMeridianUser } from '@/contexts/FeatureFlagContext';
import { logError } from '@/lib/logger';

// Constants
const ALL_SOURCES = [
  { label: 'All', value: 'unified' },
  { label: 'Arena', value: 'arena' },
  { label: 'Seed', value: 'seed', meridianOnly: true },
  { label: 'My Seed', value: 'my-seed', meridianOnly: true },
  { label: 'LLM Judge', value: 'llm-seed' },
];

const RATING_OPTIONS = [
  { label: 'Bradley-Terry', value: 'btd' as const },
  { label: 'Elo', value: 'elo' as const },
];

// Sub-components
function CIBar({
  rating,
  ciLow,
  ciHigh,
  globalMin,
  globalMax,
}: {
  rating: number;
  ciLow: number;
  ciHigh: number;
  globalMin: number;
  globalMax: number;
}) {
  const range = globalMax - globalMin;
  const barStart = ((ciLow - globalMin) / range) * 100;
  const barWidth = ((ciHigh - ciLow) / range) * 100;
  const dotPosition = ((rating - globalMin) / range) * 100;

  return (
    <div className="relative h-1.5 w-full rounded-full bg-meridian/[0.08]">
      <div
        className="absolute h-full rounded-full"
        style={{
          left: `${barStart}%`,
          width: `${barWidth}%`,
          background:
            'linear-gradient(90deg, rgba(139,35,50,0.12), rgba(139,35,50,0.2), rgba(139,35,50,0.12))',
        }}
      />
      <div
        className="absolute top-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full bg-meridian shadow-sm"
        style={{ left: `${dotPosition}%`, marginLeft: '-5px' }}
      />
    </div>
  );
}

function RankRangeTooltip() {
  const [show, setShow] = useState(false);

  return (
    <span className="relative inline-flex items-center ml-1.5">
      <button
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
        className="opacity-40 hover:opacity-70 transition-opacity"
        aria-label="What is rank range?"
      >
        <InfoIcon size={12} />
      </button>
      {show && (
        <div className="absolute z-50 left-1/2 -translate-x-1/2 top-full mt-2 px-3 py-2 rounded-lg text-xs w-52 bg-ink text-white shadow-lg">
          <div className="font-medium mb-1">Rank Range</div>
          <div className="text-white/70 leading-relaxed">
            The possible rank positions based on 95% confidence intervals.
            Overlapping CIs mean uncertain relative ranking.
          </div>
          <div className="absolute left-1/2 -translate-x-1/2 -top-1 w-2 h-2 rotate-45 bg-ink" />
        </div>
      )}
    </span>
  );
}

function SourceTabs({
  sources,
  value,
  onChange,
}: {
  sources: typeof ALL_SOURCES;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="flex items-center gap-1 p-1 rounded-xl bg-cream">
      {sources.map(src => (
        <button
          key={src.value}
          onClick={() => onChange(src.value)}
          className={`
            px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200
            ${
              value === src.value
                ? 'bg-meridian text-white shadow-button'
                : 'bg-transparent text-slate hover:text-ink'
            }
          `}
        >
          {src.label}
        </button>
      ))}
    </div>
  );
}

function StatsDisplay({
  totalBattles,
  totalModels,
}: {
  totalBattles: number;
  totalModels: number;
}) {
  return (
    <div
      className="hidden md:flex gap-12 fade-up pt-2"
      style={{ animationDelay: '0.1s' }}
    >
      <div className="text-right">
        <div className="font-serif text-4xl font-medium tabular-nums text-white">
          {totalBattles.toLocaleString()}
        </div>
        <div className="text-xs uppercase tracking-widest mt-1 text-white/50">
          Total Battles
        </div>
      </div>
      <div className="text-right">
        <div className="font-serif text-4xl font-medium tabular-nums text-white">
          {totalModels}
        </div>
        <div className="text-xs uppercase tracking-widest mt-1 text-white/50">
          Models
        </div>
      </div>
    </div>
  );
}

function ErrorState({ message }: { message: string }) {
  return (
    <div className="py-20 text-center">
      <div className="w-14 h-14 rounded-2xl mx-auto mb-4 flex items-center justify-center bg-meridian/10">
        <AlertCircleIcon size={28} color="#8B2332" />
      </div>
      <p className="text-sm font-medium text-meridian">{message}</p>
    </div>
  );
}

function EmptyRankings() {
  return (
    <div className="py-20 text-center">
      <p className="text-sm text-slate">No ranking data yet. Start voting!</p>
    </div>
  );
}

function InsufficientDataState() {
  return (
    <div className="py-20 text-center">
      <p className="font-serif text-2xl mb-3 text-ink">Insufficient Data</p>
      <p className="text-sm max-w-md mx-auto text-slate">
        Bradley-Terry ratings require more pairwise comparisons. Try Elo ratings
        or add more votes.
      </p>
    </div>
  );
}

function TableHeader() {
  return (
    <div
      className="grid items-center py-3 px-2 border-b-2 border-cream-dark"
      style={{ gridTemplateColumns: '60px 100px 1fr 280px 70px' }}
    >
      <div className="text-xs uppercase tracking-wider font-semibold text-slate">
        Rank
      </div>
      <div className="text-xs uppercase tracking-wider font-semibold text-slate flex items-center">
        <span>Rank Range</span>
        <RankRangeTooltip />
      </div>
      <div className="text-xs uppercase tracking-wider font-semibold text-slate">
        Model
      </div>
      <div className="text-xs uppercase tracking-wider font-semibold text-slate">
        Rating +/- CI
      </div>
      <div className="text-xs uppercase tracking-wider font-semibold text-slate text-right">
        Battles
      </div>
    </div>
  );
}

function TableRow({
  entry,
  rank,
  globalMin,
  globalMax,
}: {
  entry: any;
  rank: number;
  globalMin: number;
  globalMax: number;
}) {
  const isBTDInsufficient = entry.ratingType === 'btd' && entry.rating == null;
  const rankRange = entry.rankSpread
    ? `${entry.rankSpread.best}-${entry.rankSpread.worst}`
    : `${rank}`;

  return (
    <div
      className={`grid items-center px-2 py-6 transition-colors border-b border-cream-dark ${
        isBTDInsufficient ? 'opacity-50' : ''
      }`}
      style={{ gridTemplateColumns: '60px 100px 1fr 280px 70px' }}
    >
      <div className="font-serif text-lg tabular-nums text-slate">{rank}</div>
      <div className="text-sm tabular-nums text-slate">{rankRange}</div>
      <div className="flex items-center gap-3 pr-4">
        <ProviderLogo modelKey={entry.model_key} size={28} />
        <span className="font-medium text-ink">{entry.display_name}</span>
      </div>
      <div className="pr-6">
        {!isBTDInsufficient ? (
          <>
            <div className="flex items-baseline gap-2 mb-2">
              <span className="font-serif text-xl font-semibold tabular-nums text-meridian">
                {entry.rating}
              </span>
              {entry.standardError != null && (
                <span className="text-sm tabular-nums text-slate/70">
                  +/-{entry.standardError}
                </span>
              )}
            </div>
            {entry.ciLow != null && entry.ciHigh != null && (
              <>
                <CIBar
                  rating={entry.rating}
                  ciLow={entry.ciLow}
                  ciHigh={entry.ciHigh}
                  globalMin={globalMin}
                  globalMax={globalMax}
                />
                <div className="text-xs mt-1.5 tabular-nums text-slate/70">
                  95% CI: {entry.ciLow} - {entry.ciHigh}
                </div>
              </>
            )}
          </>
        ) : (
          <span className="text-sm text-slate/70">Insufficient data</span>
        )}
      </div>
      <div className="text-right tabular-nums text-ink">
        {entry.matches || 0}
      </div>
    </div>
  );
}

// Main component
export default function LeaderboardPage() {
  const isMeridianUser = useIsMeridianUser();
  const [searchParams, setSearchParams] = useSearchParams();
  const defaultSource = isMeridianUser ? 'seed' : 'unified';
  const initialSource = searchParams.get('source') || defaultSource;
  const initialJudgeConfigId = searchParams.get('judge_config_id') || '';

  const [source, setSource] = useState(initialSource);
  const [ratingType, setRatingType] = useState<'elo' | 'btd'>('btd');
  const [data, setData] = useState<any>(null);
  const [stats, setStats] = useState({ totalBattles: 0, totalModels: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [judgeConfigs, setJudgeConfigs] = useState<JudgeConfig[]>([]);
  const [selectedJudgeConfigId, setSelectedJudgeConfigId] =
    useState<string>(initialJudgeConfigId);

  const SOURCES = ALL_SOURCES.filter(
    src => !src.meridianOnly || isMeridianUser
  );

  // Force non-Meridian users to "unified" source
  useEffect(() => {
    if (!isMeridianUser) {
      if (source !== 'unified') setSource('unified');
      if (searchParams.has('source')) {
        const params = new URLSearchParams(searchParams);
        params.delete('source');
        setSearchParams(params, { replace: true });
      }
    }
  }, [isMeridianUser, source, searchParams, setSearchParams]);

  function handleSourceChange(newSource: string) {
    setSource(newSource);
    const params = new URLSearchParams(searchParams);
    params.set('source', newSource);
    if (newSource !== 'llm-seed') params.delete('judge_config_id');
    setSearchParams(params);
  }

  function handleJudgeConfigChange(newJudgeConfigId: string) {
    setSelectedJudgeConfigId(newJudgeConfigId);
    const params = new URLSearchParams(searchParams);
    params.set('judge_config_id', newJudgeConfigId);
    setSearchParams(params);
  }

  // Load judge configs
  useEffect(() => {
    async function loadJudgeConfigs() {
      if (source !== 'llm-seed') return;
      try {
        const response = await apiRequest('/api/judge-configs');
        setJudgeConfigs(response.configs);
        if (!selectedJudgeConfigId && response.configs.length > 0) {
          setSelectedJudgeConfigId(response.configs[0].id);
        }
      } catch (e) {
        logError('Error loading judge configs', e);
      }
    }
    loadJudgeConfigs();
  }, [source, selectedJudgeConfigId]);

  // Load leaderboard data
  useEffect(() => {
    async function load() {
      setLoading(true);
      setError(null);
      setData(null);

      try {
        let url = '/api/leaderboard?';
        if (source === 'llm-seed') {
          if (!selectedJudgeConfigId) {
            setLoading(false);
            return;
          }
          url = `/api/llm-judge/leaderboard?judge_config_id=${selectedJudgeConfigId}&ratingType=${ratingType}`;
        } else if (source === 'my-seed') {
          url += `source=seed&myRankings=true&ratingType=${ratingType}`;
        } else {
          url += `source=${source}&ratingType=${ratingType}`;
        }
        const j = await apiRequest(url);
        setData(j);
      } catch (e: any) {
        logError('Leaderboard fetch error', e);
        setError(e.message || 'Failed to load leaderboard data.');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [source, ratingType, selectedJudgeConfigId]);

  // Load stats
  useEffect(() => {
    async function loadStats() {
      if (source === 'llm-seed' || source === 'my-seed') {
        setStats({ totalBattles: 0, totalModels: 0 });
        return;
      }
      const data = await apiRequest(`/api/leaderboard/stats?source=${source}`);
      setStats({
        totalBattles: data.totalBattles,
        totalModels: data.totalModels,
      });
    }
    loadStats();
  }, [source]);

  // Split entries into established and pending for new models
  const allEntries = data?.entries || [];
  const establishedEntries = allEntries.filter(
    (e: any) => !e.isPendingMoreData
  );
  const pendingEntries = allEntries.filter((e: any) => e.isPendingMoreData);

  // Calculate global CI range for bar visualization (based on established models only)
  const allCILows =
    establishedEntries.map((e: any) => e.ciLow).filter((v: any) => v != null) ||
    [];
  const allCIHighs =
    establishedEntries
      .map((e: any) => e.ciHigh)
      .filter((v: any) => v != null) || [];
  const globalMin = allCILows.length > 0 ? Math.min(...allCILows) - 50 : 900;
  const globalMax = allCIHighs.length > 0 ? Math.max(...allCIHighs) + 50 : 2100;

  const allBTDInsufficient =
    ratingType === 'btd' &&
    data?.entries?.length > 0 &&
    data.entries.every((entry: any) => entry.rating == null);

  return (
    <div className="min-h-screen bg-cream">
      <style>{`
        .fade-up { animation: fade-up 0.5s ease-out forwards; opacity: 0; }
      `}</style>

      {/* Hero Header */}
      <HeroPattern>
        <div className="max-w-[900px] mx-auto px-8 pt-20 pb-20">
          <div className="flex items-start justify-between">
            <div className="fade-up">
              <h1 className="font-serif text-5xl font-medium mb-4 text-white">
                Leaderboard
              </h1>
              <p className="text-lg max-w-lg leading-relaxed text-white/70">
                Model rankings based on pairwise comparisons with statistical
                confidence intervals.
              </p>

              {isMeridianUser && (
                <Link
                  to="/leaderboard/stats?source=unified"
                  className="inline-flex items-center gap-2 mt-4 text-sm font-medium hover:opacity-80 transition-opacity text-white/90"
                >
                  <ChartIcon size={16} />
                  View Detailed Statistics (Meridian users only)
                  <ChevronRightIcon size={14} />
                </Link>
              )}
            </div>

            <StatsDisplay
              totalBattles={stats.totalBattles}
              totalModels={stats.totalModels}
            />
          </div>
        </div>
      </HeroPattern>

      {/* Controls */}
      <section className="sticky top-0 z-50 py-4 px-8 bg-cream">
        <div
          className={`max-w-[900px] mx-auto flex flex-wrap items-center gap-4 ${
            isMeridianUser ? 'justify-between' : 'justify-end'
          }`}
        >
          {isMeridianUser && (
            <SourceTabs
              sources={SOURCES}
              value={source}
              onChange={handleSourceChange}
            />
          )}

          <div className="flex items-center gap-3">
            <span className="text-xs uppercase tracking-wider font-medium text-slate">
              Rating
            </span>
            <SegmentedControl
              options={RATING_OPTIONS}
              value={ratingType}
              onChange={setRatingType}
              size="sm"
            />
          </div>
        </div>

        {source === 'llm-seed' && judgeConfigs.length > 0 && (
          <div className="max-w-[900px] mx-auto mt-3 flex items-center gap-3">
            <span className="text-xs uppercase tracking-wider font-medium text-slate">
              Judge
            </span>
            <select
              value={selectedJudgeConfigId}
              onChange={e => handleJudgeConfigChange(e.target.value)}
              className="px-4 py-2 rounded-lg text-sm font-medium bg-white border border-cream-dark text-ink"
            >
              {judgeConfigs.map(config => (
                <option key={config.id} value={config.id}>
                  {config.name} ({config.api_model})
                </option>
              ))}
            </select>
          </div>
        )}
      </section>

      {/* Main Content */}
      <section className="py-6 px-8 pb-16">
        <div className="max-w-[900px] mx-auto">
          {error ? (
            <ErrorState message={error} />
          ) : loading ? (
            <div className="py-24 flex justify-center">
              <LogoAnimated size={40} variant="default" />
            </div>
          ) : allBTDInsufficient ? (
            <InsufficientDataState />
          ) : (
            (() => {
              const allBTDInsufficient =
                ratingType === 'btd' &&
                data?.entries?.length > 0 &&
                data.entries.every((entry: any) => entry.rating == null);

              if (allBTDInsufficient) {
                return (
                  <div className="py-20 text-center">
                    <p
                      className="font-serif text-2xl mb-3"
                      style={{ color: '#1A1A1A' }}
                    >
                      Insufficient Data
                    </p>
                    <p
                      className="text-sm max-w-md mx-auto"
                      style={{ color: '#7A756E' }}
                    >
                      Bradley-Terry ratings require more pairwise comparisons.
                      Try Elo ratings or add more votes.
                    </p>
                  </div>
                );
              }

              return (
                <div className="fade-up">
                  {/* Table Header */}
                  <div
                    className="grid items-center py-3 px-2"
                    style={{
                      gridTemplateColumns: '60px 100px 1fr 280px 70px',
                      borderBottom: '2px solid #DDD8D2',
                    }}
                  >
                    <div
                      className="text-xs uppercase tracking-wider font-semibold"
                      style={{ color: '#8B8580' }}
                    >
                      Rank
                    </div>
                    <div
                      className="text-xs uppercase tracking-wider font-semibold flex items-center"
                      style={{ color: '#8B8580' }}
                    >
                      <span>Rank Range</span>
                      <RankRangeTooltip />
                    </div>
                    <div
                      className="text-xs uppercase tracking-wider font-semibold"
                      style={{ color: '#8B8580' }}
                    >
                      Model
                    </div>
                    <div
                      className="text-xs uppercase tracking-wider font-semibold"
                      style={{ color: '#8B8580' }}
                    >
                      Rating +/- CI
                    </div>
                    <div
                      className="text-xs uppercase tracking-wider font-semibold text-right"
                      style={{ color: '#8B8580' }}
                    >
                      Battles
                    </div>
                  </div>

                  {/* Table Rows - Established Models */}
                  {establishedEntries.map((entry: any, idx: number) => {
                    const rank = idx + 1;
                    const isBTDInsufficient =
                      entry.ratingType === 'btd' && entry.rating == null;
                    const rankRange = entry.rankSpread
                      ? `${entry.rankSpread.best}-${entry.rankSpread.worst}`
                      : `${rank}`;

                    return (
                      <div
                        key={entry.model_key}
                        className="grid items-center px-2 py-6 transition-colors"
                        style={{
                          gridTemplateColumns: '60px 100px 1fr 280px 70px',
                          borderBottom: '1px solid #E8E3DD',
                          opacity: isBTDInsufficient ? 0.5 : 1,
                        }}
                      >
                        {/* Rank */}
                        <div
                          className="font-serif text-lg tabular-nums"
                          style={{ color: '#6B6560' }}
                        >
                          {rank}
                        </div>

                        {/* Rank Range */}
                        <div
                          className="text-sm tabular-nums"
                          style={{ color: '#8B8580' }}
                        >
                          {rankRange}
                        </div>

                        {/* Model Name */}
                        <div className="flex items-center gap-3 pr-4">
                          <ProviderLogo modelKey={entry.model_key} size={28} />
                          <span
                            className="font-medium"
                            style={{ color: '#1A1A1A' }}
                          >
                            {entry.display_name}
                          </span>
                        </div>

                        {/* Rating + CI */}
                        <div className="pr-6">
                          {!isBTDInsufficient ? (
                            <>
                              <div className="flex items-baseline gap-2 mb-2">
                                <span
                                  className="font-serif text-xl font-semibold tabular-nums"
                                  style={{ color: '#8B2332' }}
                                >
                                  {entry.rating}
                                </span>
                                {entry.standardError != null && (
                                  <span
                                    className="text-sm tabular-nums"
                                    style={{ color: '#A09A94' }}
                                  >
                                    +/-{entry.standardError}
                                  </span>
                                )}
                              </div>

                              {/* CI Bar */}
                              {entry.ciLow != null && entry.ciHigh != null && (
                                <>
                                  <CIBar
                                    rating={entry.rating}
                                    ciLow={entry.ciLow}
                                    ciHigh={entry.ciHigh}
                                    globalMin={globalMin}
                                    globalMax={globalMax}
                                  />
                                  <div
                                    className="text-xs mt-1.5 tabular-nums"
                                    style={{ color: '#A09A94' }}
                                  >
                                    95% CI: {entry.ciLow} - {entry.ciHigh}
                                  </div>
                                </>
                              )}
                            </>
                          ) : (
                            <span
                              className="text-sm"
                              style={{ color: '#A09A94' }}
                            >
                              Insufficient data
                            </span>
                          )}
                        </div>

                        {/* Battles */}
                        <div
                          className="text-right tabular-nums"
                          style={{ color: '#1A1A1A' }}
                        >
                          {entry.matches || 0}
                        </div>
                      </div>
                    );
                  })}

                  {/* Pending More Battles Section */}
                  {pendingEntries.length > 0 && (
                    <>
                      {/* Section Header */}
                      <div
                        className="py-4 px-2 mt-4"
                        style={{
                          borderTop: '2px solid #DDD8D2',
                          borderBottom: '1px solid #E8E3DD',
                        }}
                      >
                        <div className="flex items-center gap-2">
                          <span
                            className="text-xs uppercase tracking-wider font-semibold"
                            style={{ color: '#8B8580' }}
                          >
                            Pending More Battles
                          </span>
                          <span
                            className="text-xs px-1.5 py-0.5 rounded font-medium"
                            style={{
                              background: 'rgba(139,35,50,0.1)',
                              color: '#8B2332',
                            }}
                          >
                            {pendingEntries.length} model
                            {pendingEntries.length !== 1 ? 's' : ''}
                          </span>
                        </div>
                        <p
                          className="text-xs mt-1"
                          style={{ color: '#A09A94' }}
                        >
                          New models need at least {MIN_MATCHES_FOR_RANKING}{' '}
                          battles before their rankings stabilize.
                        </p>
                      </div>

                      {/* Pending Model Rows */}
                      {pendingEntries.map((entry: any) => (
                        <div
                          key={entry.model_key}
                          className="grid items-center px-2 py-6 transition-colors"
                          style={{
                            gridTemplateColumns: '60px 100px 1fr 280px 70px',
                            borderBottom: '1px solid #E8E3DD',
                            opacity: 0.7,
                          }}
                        >
                          {/* Rank placeholder */}
                          <div
                            className="font-serif text-lg tabular-nums"
                            style={{ color: '#A09A94' }}
                          >
                            —
                          </div>

                          {/* Rank Range placeholder */}
                          <div
                            className="text-sm tabular-nums"
                            style={{ color: '#A09A94' }}
                          >
                            —
                          </div>

                          {/* Model Name with New! badge */}
                          <div className="flex items-center gap-3 pr-4">
                            <ProviderLogo
                              modelKey={entry.model_key}
                              size={28}
                            />
                            <span
                              className="font-medium"
                              style={{ color: '#1A1A1A' }}
                            >
                              {entry.display_name}
                            </span>
                            <span
                              className="text-xs px-1.5 py-0.5 rounded-full font-semibold"
                              style={{
                                background:
                                  'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                color: '#FFF',
                                boxShadow: '0 1px 3px rgba(16,185,129,0.3)',
                              }}
                            >
                              New!
                            </span>
                          </div>

                          {/* Rating + CI - show progress towards MIN_MATCHES_FOR_RANKING */}
                          <div className="pr-6">
                            <div className="flex items-center gap-2">
                              <span
                                className="text-sm font-medium tabular-nums"
                                style={{ color: '#8B2332' }}
                              >
                                {Math.max(
                                  0,
                                  MIN_MATCHES_FOR_RANKING - (entry.matches || 0)
                                )}{' '}
                                more battles needed
                              </span>
                            </div>
                            {/* Progress bar */}
                            <div
                              className="mt-2 h-1.5 w-full rounded-full overflow-hidden"
                              style={{ background: 'rgba(139,35,50,0.1)' }}
                            >
                              <div
                                className="h-full rounded-full transition-all duration-300"
                                style={{
                                  width: `${Math.min(100, ((entry.matches || 0) / MIN_MATCHES_FOR_RANKING) * 100)}%`,
                                  background:
                                    'linear-gradient(90deg, #10b981, #059669)',
                                }}
                              />
                            </div>
                            <div
                              className="text-xs mt-1 tabular-nums"
                              style={{ color: '#A09A94' }}
                            >
                              {entry.matches || 0} / {MIN_MATCHES_FOR_RANKING}{' '}
                              battles
                            </div>
                          </div>

                          {/* Battles */}
                          <div
                            className="text-right tabular-nums"
                            style={{ color: '#1A1A1A' }}
                          >
                            {entry.matches || 0}
                          </div>
                        </div>
                      ))}
                    </>
                  )}

                  {allEntries.length === 0 && (
                    <div className="py-20 text-center">
                      <p className="text-sm" style={{ color: '#7A756E' }}>
                        No ranking data yet. Start voting!
                      </p>
                    </div>
                  )}
                </div>
              );
            })()
          )}
        </div>
      </section>

      {/* Methodology footer */}
      <section className="py-6 px-8 border-t border-cream-dark">
        <div className="max-w-[900px] mx-auto flex items-center justify-between">
          <p className="text-xs text-slate/70">
            Rankings use a variation of the{' '}
            <a
              href="https://en.wikipedia.org/wiki/Bradley%E2%80%93Terry_model"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:no-underline font-medium text-meridian"
            >
              Bradley-Terry
            </a>{' '}
            model. Rank ranges use the resulting confidence intervals, following
            LMArena's documented{' '}
            <a
              href="https://news.lmarena.ai/ranking-method/"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:no-underline font-medium text-meridian"
            >
              method
            </a>
            .
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
